import { ClearIcon, Dialog, DialogContent, Grid, styledComponent, Typography, useTheme } from '@hermes/web-components'
import React, { useCallback, useMemo } from 'react'
import { OnChangeSearchType, SearchType } from '../../SearchPage/common/types'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { SearchTypes } from '../../../providers/SearchProfilesPage/types'
import NextImage from 'next/image'

interface MobileSearchTypeModalProps {
  isOpen: boolean
  onClose: () => void
  selectedSearchType: SearchType
  onChangeSearchType: OnChangeSearchType
  searchTypes: SearchType[]
}

const StyledDialog = styledComponent(Dialog)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    '& .MuiDialog-paper': {
      margin: 0,
      width: '100%',
      height: '100%',
      borderRadius: 0,
      maxHeight: 'none'
    },
    '& .MuiDialogContent-root': {
      width: '100%'
    }
  }
}))

const MobileSearchTypeModal = ({
  isOpen,
  onClose,
  selectedSearchType,
  onChangeSearchType,
  searchTypes
}: MobileSearchTypeModalProps) => {
  const translate = useTranslateMessage()

  const theme = useTheme()
  const themedBlue = theme?.custom?.SearchBar?.searchTypeCardColor ?? 'system.blue'

  const handleSearchType = useCallback(
    (value: SearchTypes) => () => {
      onClose()
      onChangeSearchType(value)
    },
    [onClose, onChangeSearchType]
  )

  const searchTypesToShow = useMemo(() => [searchTypes.slice(0, 4), searchTypes.slice(4)], [searchTypes])

  const commonWhiteString = 'common.white'

  return (
    <StyledDialog open={isOpen} onClose={onClose} maxWidth="lg">
      <DialogContent
        sx={{
          px: 2,
          pt: 8,
          width: '100vw',
          backgroundColor:
            theme?.custom?.SearchBar?.mobile?.profileSelectModal?.backgroundColor ?? 'background.default',
          position: 'relative'
        }}
      >
        <ClearIcon onClick={onClose} sx={{ cursor: 'pointer', position: 'absolute', right: 16, top: 24 }} />
        <Typography sx={{ ...(theme?.custom?.SearchBar?.mobile?.profileSelectModal?.title ?? {}) }} variant="h2">
          {translate('search.your_search')}
        </Typography>
        <Grid mt={7} gap="8px" flexWrap="nowrap" container>
          {searchTypesToShow[0].map(({ title, value, Icon }) => (
            <Grid
              item
              key={value}
              onClick={handleSearchType(value)}
              container
              xs={3}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{
                px: '6px',
                py: '12px',
                border: '2px solid',
                borderColor: selectedSearchType.value === value ? themedBlue : commonWhiteString,
                borderRadius: '4px',
                backgroundColor: commonWhiteString,
                ...(theme?.custom?.SearchBar?.mobile?.profileSelectModal?.profileSelectItem ?? {})
              }}
            >
              <NextImage
                src={`/hermes-static/icons/${Icon}.svg`}
                alt="search-type-icon"
                width={22}
                height={22}
                objectFit="none"
                unoptimized
              />
              <Typography color={themedBlue} textAlign="center" mt="4px" variant="caption">
                {title}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Grid mt={7} gap="8px" flexWrap="nowrap" container>
          {searchTypesToShow[1].map(({ title, value, Icon }) => (
            <Grid
              item
              key={value}
              onClick={handleSearchType(value)}
              container
              xs={3}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{
                px: '6px',
                py: '12px',
                border: '2px solid',
                borderColor: selectedSearchType.value === value ? themedBlue : commonWhiteString,
                borderRadius: '4px',
                backgroundColor: commonWhiteString,
                ...(theme?.custom?.SearchBar?.mobile?.profileSelectModal?.profileSelectItem ?? {})
              }}
            >
              <NextImage
                src={`/hermes-static/icons/${Icon}.png`}
                alt="search-type-icon"
                width={22}
                height={22}
                objectFit="none"
              />
              <Typography color={themedBlue} textAlign="center" mt="4px" variant="caption">
                {title}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Typography
          onClick={onClose}
          textAlign="center"
          color={themedBlue}
          mt={2}
          sx={{ ...(theme?.custom?.SearchBar?.mobile?.profileSelectModal?.backButton ?? {}) }}
        >
          {translate('search.back_to_search_results')}
        </Typography>
      </DialogContent>
    </StyledDialog>
  )
}

export default MobileSearchTypeModal
